import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import LazyImage from '../lazy-image';
import { format } from 'date-fns';
import { Link } from 'gatsby';
import parseStoryblokLink from '../../utils/parseStoryblokLink';
import { motion } from 'framer-motion';
import mq from '../../utils/mq';

const BlogItemCardContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  grid-column: span 1;
  grid-row: span 1;
  height: 100%;
  &.first-item {
    grid-column: span 2;
    grid-row: span 2;
  }
`;

const BlogItemCardImage = styled(Link)`
  width: 100%;
  aspect-ratio: 16/9;
  overflow: hidden;
  position: relative;
  border-radius: var(--Radius-Radius-3, 16px);
  background: var(--Color-Grey-1, #dee2e4);
`;

const BlogItemCardContentContainer = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
  text-decoration: none;
`;

const BlogItemCardContent = styled.span`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BlogItemCardTitle = styled.h3`
  color: #161616;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
  overflow: hidden;
  text-overflow: ellipsis;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  &.first-item {
    font-size: 27px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%;
  }
  ${mq.mobile(css`
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 140%;
    &.first-item {
      font-size: 24px;
    }
  `)};
`;

const BlogItemCardDescription = styled.p`
  color: #666;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  display: none;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  .first-item & {
    display: block;
    display: -webkit-box;
  }
  ${mq.mobile(css`
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    display: block;
    display: -webkit-box;
  `)};
`;

const BlogItemCardMeta = styled.span`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const BlogItemCategory = styled.span`
  color: #161616;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;

const BlogItemPublishDate = styled.span`
  color: #666;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
`;

export default function BlogItemCard({
  blogItem,
  firstItem
}: {
  blogItem: any;
  firstItem: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <BlogItemCardContainer
      className={firstItem ? 'first-item' : ''}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <BlogItemCardImage
        className={firstItem ? 'first-item' : ''}
        to={`/${blogItem.full_slug}`}
      >
        <motion.div
          style={{ height: '100%', width: '100%', willChange: 'transform' }}
          animate={
            isHovered
              ? {
                  scale: 1.03,
                  transition: { duration: 0.6, ease: [0.26, 1, 0.5, 1] }
                }
              : {
                  scale: 1,
                  transition: { duration: 0.3, ease: [0.26, 1, 0.5, 1] }
                }
          }
        >
          <LazyImage
            fluid={getFluidGatsbyImage(blogItem.content.gridImage)}
            width="100%"
            height="100%"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </motion.div>
      </BlogItemCardImage>
      <BlogItemCardContentContainer to={`/${blogItem.full_slug}`}>
        <BlogItemCardContent>
          <BlogItemCardTitle className={firstItem ? 'first-item' : ''}>
            {blogItem.content.customTitle}
          </BlogItemCardTitle>
          <BlogItemCardDescription className={firstItem ? 'first-item' : ''}>
            {blogItem.content.intro}
          </BlogItemCardDescription>
        </BlogItemCardContent>
        <BlogItemCardMeta>
          <BlogItemCategory>{blogItem.content.category.name}</BlogItemCategory>
          <BlogItemPublishDate>
            {format(new Date(blogItem.first_published_at), 'dd MMM, yyyy')}
          </BlogItemPublishDate>
        </BlogItemCardMeta>
      </BlogItemCardContentContainer>
    </BlogItemCardContainer>
  );
}
